/**
 * AdvancedSearchDialog — a recursive nested AND/OR query builder. The root is a
 * group; groups nest (bounded to match the server) and hold conditions or
 * sub-groups. "Search" hands the built tree to the caller, which runs it and
 * shows the results in the message list.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, X } from 'lucide-react';
import type {
  AdvancedSearchGroup,
  AdvancedSearchNode,
  AdvancedSearchCondition,
  AdvancedSearchField,
  AdvancedSearchOperator,
} from '@/lib/api';

// Root is depth 0; a group can spawn a sub-group while depth < MAX_GROUP_DEPTH,
// keeping the deepest leaf within the server's ADV_SEARCH_MAX_DEPTH (5).
const MAX_GROUP_DEPTH = 4;

const FIELD_LABELS: Record<AdvancedSearchField, string> = {
  from: 'From',
  recipient: 'Recipient',
  subject: 'Subject',
  body: 'Body',
  has_attachment: 'Has attachment',
  is_read: 'Read state',
  is_starred: 'Starred',
  date_before: 'Date before',
  date_after: 'Date after',
};
const FIELDS = Object.keys(FIELD_LABELS) as AdvancedSearchField[];
const OPERATORS: AdvancedSearchOperator[] = [
  'contains',
  'not_contains',
  'equals',
  'starts_with',
  'ends_with',
];

const isBoolField = (f: AdvancedSearchField) =>
  f === 'has_attachment' || f === 'is_read' || f === 'is_starred';
const isDateField = (f: AdvancedSearchField) => f === 'date_before' || f === 'date_after';

const emptyCondition = (): AdvancedSearchCondition => ({
  type: 'condition',
  field: 'from',
  operator: 'contains',
  value: '',
});
const emptyGroup = (): AdvancedSearchGroup => ({
  type: 'group',
  combinator: 'and',
  nodes: [emptyCondition()],
});

/** Count leaf conditions that can actually match (Search stays disabled at 0). */
export function usableConditionCount(node: AdvancedSearchNode): number {
  if (node.type === 'group') {
    return node.nodes.reduce((sum, n) => sum + usableConditionCount(n), 0);
  }
  if (isBoolField(node.field) || isDateField(node.field)) return node.value.trim() ? 1 : 0;
  return node.value.trim() ? 1 : 0;
}

/** Labels for a boolean field's two values (present/absent, read/unread, …). */
function boolOptions(field: AdvancedSearchField): { yes: string; no: string } {
  if (field === 'is_read') return { yes: 'is read', no: 'is unread' };
  if (field === 'is_starred') return { yes: 'is starred', no: 'is not starred' };
  return { yes: 'is present', no: 'is absent' };
}

function ConditionEditor({
  condition,
  onChange,
  onRemove,
}: {
  condition: AdvancedSearchCondition;
  onChange: (c: AdvancedSearchCondition) => void;
  onRemove: () => void;
}) {
  const setField = (field: AdvancedSearchField) => {
    if (isBoolField(field)) onChange({ type: 'condition', field, value: 'true' });
    else if (isDateField(field)) onChange({ type: 'condition', field, value: '' });
    else
      onChange({ type: 'condition', field, operator: condition.operator ?? 'contains', value: '' });
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <select
        className="h-8 rounded-md border bg-background px-2"
        aria-label="Field"
        value={condition.field}
        onChange={(e) => setField(e.target.value as AdvancedSearchField)}
      >
        {FIELDS.map((f) => (
          <option key={f} value={f}>
            {FIELD_LABELS[f]}
          </option>
        ))}
      </select>
      {isBoolField(condition.field) ? (
        <select
          className="h-8 rounded-md border bg-background px-2"
          aria-label="Value"
          value={condition.value}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
        >
          <option value="true">{boolOptions(condition.field).yes}</option>
          <option value="false">{boolOptions(condition.field).no}</option>
        </select>
      ) : isDateField(condition.field) ? (
        <input
          type="date"
          className="h-8 rounded-md border bg-background px-2"
          aria-label="Date"
          value={condition.value}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
        />
      ) : (
        <>
          <select
            className="h-8 rounded-md border bg-background px-2"
            aria-label="Operator"
            value={condition.operator ?? 'contains'}
            onChange={(e) =>
              onChange({ ...condition, operator: e.target.value as AdvancedSearchOperator })
            }
          >
            {OPERATORS.map((o) => (
              <option key={o} value={o}>
                {o.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <Input
            className="w-40"
            placeholder="value"
            value={condition.value}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
          />
        </>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        title="Remove condition"
        aria-label="Remove condition"
        onClick={onRemove}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function GroupEditor({
  group,
  onChange,
  onRemove,
  depth,
}: {
  group: AdvancedSearchGroup;
  onChange: (g: AdvancedSearchGroup) => void;
  onRemove?: () => void;
  depth: number;
}) {
  const setNode = (i: number, node: AdvancedSearchNode) =>
    onChange({ ...group, nodes: group.nodes.map((n, idx) => (idx === i ? node : n)) });
  const removeNode = (i: number) =>
    onChange({ ...group, nodes: group.nodes.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-2 rounded-md border p-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Match</span>
        <select
          className="h-8 rounded-md border bg-background px-2 text-sm"
          aria-label="Combinator"
          value={group.combinator}
          onChange={(e) => onChange({ ...group, combinator: e.target.value as 'and' | 'or' })}
        >
          <option value="and">ALL</option>
          <option value="or">ANY</option>
        </select>
        <span className="text-xs text-muted-foreground">of the following:</span>
        <span className="flex-1" />
        {onRemove && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            title="Remove group"
            aria-label="Remove group"
            onClick={onRemove}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div className="space-y-2 border-l pl-3">
        {group.nodes.length === 0 && (
          <p className="text-xs text-muted-foreground">Empty group — add a condition.</p>
        )}
        {group.nodes.map((n, i) =>
          n.type === 'group' ? (
            <GroupEditor
              key={i}
              group={n}
              onChange={(g) => setNode(i, g)}
              onRemove={() => removeNode(i)}
              depth={depth + 1}
            />
          ) : (
            <ConditionEditor
              key={i}
              condition={n}
              onChange={(c) => setNode(i, c)}
              onRemove={() => removeNode(i)}
            />
          ),
        )}
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="gap-1"
          onClick={() => onChange({ ...group, nodes: [...group.nodes, emptyCondition()] })}
        >
          <Plus className="h-3.5 w-3.5" /> Condition
        </Button>
        {depth < MAX_GROUP_DEPTH && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => onChange({ ...group, nodes: [...group.nodes, emptyGroup()] })}
          >
            <Plus className="h-3.5 w-3.5" /> Group
          </Button>
        )}
      </div>
    </div>
  );
}

export interface AdvancedSearchAccount {
  accountId: string;
  emailAddress: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Seed the builder — e.g. re-editing the active advanced search. */
  initialQuery?: AdvancedSearchGroup | null;
  /** The caller's connected accounts, for the "search in accounts" picker. */
  accounts: AdvancedSearchAccount[];
  /** Seed the account selection (undefined = all accounts). */
  initialAccountIds?: string[];
  onSearch: (query: AdvancedSearchGroup, accountIds: string[] | undefined) => void;
}

export function AdvancedSearchDialog({
  open,
  onOpenChange,
  initialQuery,
  accounts,
  initialAccountIds,
  onSearch,
}: Props) {
  const [root, setRoot] = useState<AdvancedSearchGroup>(() => initialQuery ?? emptyGroup());
  // Which accounts to search — defaults to all (or the active search's subset).
  const [accountIds, setAccountIds] = useState<Set<string>>(
    () => new Set(initialAccountIds ?? accounts.map((a) => a.accountId)),
  );

  // Reseed each time the dialog opens (fresh, or from the active query for
  // editing). Intentionally keyed on the `open` transition only — the current
  // props are read at that moment, not tracked as dependencies.
  useEffect(() => {
    if (open) {
      setRoot(initialQuery ?? emptyGroup());
      setAccountIds(new Set(initialAccountIds ?? accounts.map((a) => a.accountId)));
    }
  }, [open]);

  const count = usableConditionCount(root);
  const showAccountPicker = accounts.length > 1;
  const allAccountsSelected =
    accounts.length > 0 && accounts.every((a) => accountIds.has(a.accountId));

  const toggleAccount = (accountId: string, checked: boolean) =>
    setAccountIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(accountId);
      else next.delete(accountId);
      return next;
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-2xl flex flex-col">
        <DialogHeader>
          <DialogTitle>Advanced search</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {showAccountPicker && (
            <div className="space-y-1.5 rounded-md border p-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  Search in accounts
                </span>
                <button
                  type="button"
                  className="text-[11px] text-primary hover:underline"
                  onClick={() =>
                    setAccountIds(
                      allAccountsSelected ? new Set() : new Set(accounts.map((a) => a.accountId)),
                    )
                  }
                >
                  {allAccountsSelected ? 'Clear all' : 'Select all'}
                </button>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {accounts.map((a) => (
                  <label key={a.accountId} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      className="h-4 w-4"
                      checked={accountIds.has(a.accountId)}
                      onCheckedChange={(c) => toggleAccount(a.accountId, c)}
                    />
                    <span className="truncate">{a.emailAddress}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <GroupEditor group={root} onChange={setRoot} depth={0} />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={count === 0 || (accounts.length > 0 && accountIds.size === 0)}
            onClick={() => {
              // All selected (or no accounts to pick) → undefined = search every
              // account; a subset → the explicit list.
              const ids = accounts
                .filter((a) => accountIds.has(a.accountId))
                .map((a) => a.accountId);
              onSearch(root, allAccountsSelected || accounts.length === 0 ? undefined : ids);
              onOpenChange(false);
            }}
          >
            Search
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
